// File: src/components/Admin/InternetVendorManagementV2.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Speed as SpeedIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Router as RouterIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// CSS to hide number input spinners
const numberInputStyle = {
  '& input[type=number]': {
    'MozAppearance': 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button': {
    'WebkitAppearance': 'none',
    margin: 0,
  },
  '& input[type=number]::-webkit-inner-spin-button': {
    'WebkitAppearance': 'none',
    margin: 0,
  },
};

interface Vendor {
  id: number;
  vendor_name: string;
  vendor_code: string;
  contact_email: string;
  contact_phone: string;
  support_phone: string;
  is_active: boolean;
  default_tax_code?: string;
  default_tax_rate?: number;
  service_count?: number;
}

interface VendorService {
  id: number;
  vendor_id: number;
  vendor_name?: string;
  service_type: 'vdsl' | 'fibre' | 'cable';
  speed_down: number;
  speed_up: number;
  speed_label: string;
  technology_details: string;
  wholesale_monthly_cost: number;
  wholesale_setup_fee: number;
  wholesale_modem_cost: number;
  is_active: boolean;
  retail_service?: RetailService;
}

interface RetailService {
  id: number;
  vendor_service_id: number;
  retail_name: string;
  retail_description: string;
  retail_monthly_price: number;
  retail_setup_fee: number;
  monthly_margin: number;
  margin_percentage: number;
  is_active: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Service form type with string values
type ServiceFormType = {
  vendor_id: number;
  service_type: 'vdsl' | 'fibre' | 'cable';
  speed_down: string;
  speed_up: string;
  speed_label: string;
  technology_details: string;
  wholesale_monthly_cost: string;
  wholesale_setup_fee: string;
  wholesale_modem_cost: string;
  retail_monthly_price: string;
  retail_setup_fee: string;
  retail_name: string;
  retail_description: string;
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface TaxOverride {
  id: number;
  customer_id: number;
  vendor_id: number;
  tax_code: string;
  tax_rate: number;
  vendor_name?: string;
  company?: string;
}

interface Customer {
  id: number;
  company: string;
  city?: string;
  province?: string;
}

const InternetVendorManagementV2: React.FC = () => {
  const { member } = useAuth();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorServices, setVendorServices] = useState<VendorService[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [taxOverrides, setTaxOverrides] = useState<TaxOverride[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [taxOverrideDialog, setTaxOverrideDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  
  // Helper function to safely convert to number
  const toNumber = (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return parseFloat(value) || 0;
    return 0;
  };
  
  // Dialog states
  const [vendorDialog, setVendorDialog] = useState(false);
  const [serviceDialog, setServiceDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [selectedService, setSelectedService] = useState<VendorService | null>(null);
  
  // Expanded vendor cards
  const [expandedVendors, setExpandedVendors] = useState<Set<number>>(new Set());
  
  // Form data  
  const [vendorForm, setVendorForm] = useState({
    vendor_name: '',
    vendor_code: '',
    contact_email: '',
    contact_phone: '',
    support_phone: '',
    default_tax_code: 'HST',
    default_tax_rate: 13,
    is_active: true,
  });
  
  const [serviceForm, setServiceForm] = useState<ServiceFormType>({
    vendor_id: 0,
    service_type: 'vdsl',
    speed_down: '',
    speed_up: '',
    speed_label: '',
    technology_details: '',
    wholesale_monthly_cost: '',
    wholesale_setup_fee: '',
    wholesale_modem_cost: '',
    retail_monthly_price: '',
    retail_setup_fee: '',
    retail_name: '',
    retail_description: '',
  });

  useEffect(() => {
    loadData();
    if (tabValue === 3) {
      loadTaxData();
    }
  }, [tabValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load vendors first
      const vendorsRes = await api.get('/endpoints/internetvendors');
      setVendors(vendorsRes.data.data || []);
      
      // Then try to load services
      try {
        const servicesRes = await api.get('/endpoints/internetvendors/services');
        setVendorServices(servicesRes.data.data || []);
      } catch (servicesErr: any) {
        console.warn('Failed to load services, continuing without them:', servicesErr.message);
        setVendorServices([]);
      }
      
    } catch (err: any) {
      console.error('Failed to load vendors:', err);
      setError(err.response?.data?.message || 'Failed to load vendors');
      setVendors([]);
      setVendorServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleVendorExpanded = (vendorId: number) => {
    const newExpanded = new Set(expandedVendors);
    if (newExpanded.has(vendorId)) {
      newExpanded.delete(vendorId);
    } else {
      newExpanded.add(vendorId);
    }
    setExpandedVendors(newExpanded);
  };

  const handleAddVendor = () => {
    setSelectedVendor(null);
    setVendorForm({
      vendor_name: '',
      vendor_code: '',
      contact_email: '',
      contact_phone: '',
      support_phone: '',
      default_tax_code: 'HST',
      default_tax_rate: 13,
      is_active: true,
    });
    setVendorDialog(true);
  };

  const handleEditVendor = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setVendorForm({
      vendor_name: vendor.vendor_name,
      vendor_code: vendor.vendor_code,
      contact_email: vendor.contact_email,
      contact_phone: vendor.contact_phone,
      support_phone: vendor.support_phone,
      default_tax_code: vendor.default_tax_code || 'HST',
      default_tax_rate: vendor.default_tax_rate || 13,
      is_active: vendor.is_active,
    });
    setVendorDialog(true);
  };

  const handleDeleteVendor = async (vendor: Vendor) => {
    if (!window.confirm(`Are you sure you want to delete "${vendor.vendor_name}"? This will also delete all associated services.`)) {
      return;
    }
    
    try {
      await api.delete(`/endpoints/internetvendors/${vendor.id}`);
      setSuccess('Vendor deleted successfully');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete vendor');
    }
  };

  const handleSaveVendor = async () => {
    // Validate required fields
    if (!vendorForm.vendor_name.trim()) {
      setError('Vendor name is required');
      return;
    }
    
    setSaving(true);
    try {
      setError(null);
      
      // Clean up form data - convert empty strings to null
      const cleanData = {
        ...vendorForm,
        contact_email: vendorForm.contact_email || null,
        contact_phone: vendorForm.contact_phone || null,
        support_phone: vendorForm.support_phone || null,
        vendor_code: vendorForm.vendor_code || null,
        default_tax_code: vendorForm.default_tax_code,
        default_tax_rate: vendorForm.default_tax_rate,
      };
      
      console.log('Sending vendor data:', cleanData);
      
      if (selectedVendor) {
        await api.put(`/endpoints/internetvendors/${selectedVendor.id}`, cleanData);
        setSuccess('Vendor updated successfully');
      } else {
        await api.post('/endpoints/internetvendors', cleanData);
        setSuccess('Vendor created successfully');
      }
      setVendorDialog(false);
      loadData();
    } catch (err: any) {
      console.error('Save vendor error:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.message || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleAddService = (vendor: Vendor) => {
    setSelectedService(null);
    setServiceForm({
      vendor_id: vendor.id,
      service_type: 'vdsl',
      speed_down: '',
      speed_up: '',
      speed_label: '',
      technology_details: '',
      wholesale_monthly_cost: '',
      wholesale_setup_fee: '',
      wholesale_modem_cost: '',
      retail_monthly_price: '',
      retail_setup_fee: '',
      retail_name: '',
      retail_description: '',
    });
    setServiceDialog(true);
  };

  const handleEditService = (service: VendorService) => {
    setSelectedService(service);
    const baseRetailPrice = service.retail_service?.retail_monthly_price || 
      (service.wholesale_monthly_cost + getDefaultMargin(service.speed_down));
    const baseRetailSetup = service.retail_service?.retail_setup_fee || 
      (service.wholesale_setup_fee + 50);
    
    setServiceForm({
      vendor_id: service.vendor_id,
      service_type: service.service_type,
      speed_down: String(service.speed_down),
      speed_up: String(service.speed_up),
      speed_label: service.speed_label,
      technology_details: service.technology_details,
      wholesale_monthly_cost: String(service.wholesale_monthly_cost),
      wholesale_setup_fee: String(service.wholesale_setup_fee),
      wholesale_modem_cost: String(service.wholesale_modem_cost),
      retail_monthly_price: String(baseRetailPrice),
      retail_setup_fee: String(baseRetailSetup),
      retail_name: service.retail_service?.retail_name || generateRetailName(service),
      retail_description: service.retail_service?.retail_description || generateRetailDescription(service),
    });
    setServiceDialog(true);
  };

  const getDefaultMargin = (speed: number): number => {
    if (speed <= 30) return 14.95;
    if (speed <= 150) return 19.95;
    return 24.95;
  };

  const generateRetailName = (service: Partial<VendorService> | ServiceFormType): string => {
    const typeNames = {
      vdsl: 'High-Speed Internet',
      fibre: 'Fibre Internet',
      cable: 'Cable Internet',
    };
    return `${typeNames[service.service_type!]} ${service.speed_label}`;
  };

  const generateRetailDescription = (service: Partial<VendorService> | ServiceFormType): string => {
    const typeDesc = {
      vdsl: 'DSL',
      fibre: 'fiber-optic',
      cable: 'cable',
    };
    const speedDown = typeof service.speed_down === 'string' ? toNumber(service.speed_down) : service.speed_down;
    const speedUp = typeof service.speed_up === 'string' ? toNumber(service.speed_up) : service.speed_up;
    return `Reliable ${typeDesc[service.service_type!]} internet with speeds up to ${speedDown}Mbps download and ${speedUp}Mbps upload`;
  };

  const handleServiceFormChange = (field: string, value: any) => {
    const updatedForm = { ...serviceForm, [field]: value };
    
    // Auto-generate speed label if speeds change
    if (field === 'speed_down' || field === 'speed_up') {
      const speed = field === 'speed_down' ? toNumber(value) : toNumber(updatedForm.speed_down);
      if (speed > 0) {
        updatedForm.speed_label = speed >= 1000 ? '1GB' : `${speed}M`;
      }
    }
    
    // Auto-calculate retail pricing when wholesale cost changes
    if (field === 'wholesale_monthly_cost' && value !== '') {
      const margin = getDefaultMargin(toNumber(updatedForm.speed_down));
      updatedForm.retail_monthly_price = String(toNumber(value) + margin);
    }
    
    if (field === 'wholesale_setup_fee' && value !== '') {
      updatedForm.retail_setup_fee = String(toNumber(value) + 50);
    }
    
    // Auto-generate retail name and description
    if (['service_type', 'speed_down', 'speed_up', 'speed_label'].includes(field)) {
      updatedForm.retail_name = generateRetailName(updatedForm);
      updatedForm.retail_description = generateRetailDescription(updatedForm);
    }
    
    setServiceForm(updatedForm);
  };

  const handleSaveService = async () => {
    try {
      const payload = {
        vendor_id: serviceForm.vendor_id,
        service_type: serviceForm.service_type,
        speed_down: toNumber(serviceForm.speed_down),
        speed_up: toNumber(serviceForm.speed_up),
        speed_label: serviceForm.speed_label,
        technology_details: serviceForm.technology_details,
        wholesale_monthly_cost: toNumber(serviceForm.wholesale_monthly_cost),
        wholesale_setup_fee: toNumber(serviceForm.wholesale_setup_fee),
        wholesale_modem_cost: toNumber(serviceForm.wholesale_modem_cost),
        retail_monthly_price: toNumber(serviceForm.retail_monthly_price),
        retail_setup_fee: toNumber(serviceForm.retail_setup_fee),
        retail_name: serviceForm.retail_name,
        retail_description: serviceForm.retail_description,
        is_active: true,
      };
      
      if (selectedService) {
        await api.put(`/endpoints/internetvendors/services/${selectedService.id}`, payload);
        setSuccess('Service updated successfully');
      } else {
        await api.post('/endpoints/internetvendors/services', payload);
        setSuccess('Service created successfully with retail pricing');
      }
      setServiceDialog(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save service');
    }
  };

  const handleDeleteService = async (serviceId: number) => {
    if (!window.confirm('Are you sure you want to delete this service? This will also remove the retail service.')) return;
    
    try {
      await api.delete(`/endpoints/internetvendors/services/${serviceId}`);
      setSuccess('Service deleted successfully');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete service');
    }
  };

  const calculateMargin = (retail: number, wholesale: number): number => {
    return ((retail - wholesale) / wholesale * 100);
  };

  const VendorServicesDisplay = ({ vendor }: { vendor: Vendor }) => {
    const vendorServicesList = vendorServices.filter(s => s.vendor_id === vendor.id);
    
    return (
      <Box sx={{ mt: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1">Services & Pricing</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleAddService(vendor)}
          >
            Add Service
          </Button>
        </Box>
        
        {vendorServicesList.length === 0 ? (
          <Alert severity="info">No services configured for this vendor</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Speed</TableCell>
                  <TableCell>Wholesale</TableCell>
                  <TableCell>Retail</TableCell>
                  <TableCell>Margin</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorServicesList.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {service.retail_service?.retail_name || service.technology_details}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {service.service_type.toUpperCase()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{service.speed_label}</TableCell>
                    <TableCell>${service.wholesale_monthly_cost}/mo</TableCell>
                    <TableCell>
                      ${service.retail_service?.retail_monthly_price || 'N/A'}/mo
                    </TableCell>
                    <TableCell>
                      {service.retail_service ? (
                        <Chip
                          label={`${toNumber(service.retail_service.margin_percentage).toFixed(1)}%`}
                          size="small"
                          color={toNumber(service.retail_service.margin_percentage) > 30 ? 'success' : 'warning'}
                        />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEditService(service)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteService(service.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  const VendorsTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Internet Service Vendors</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddVendor}
        >
          Add Vendor
        </Button>
      </Box>

      <Grid container spacing={3}>
        {vendors.map((vendor) => (
          <Grid item xs={12} key={vendor.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start">
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {vendor.vendor_name}
                    </Typography>
                    <Box display="flex" gap={2} mb={1}>
                      <Chip 
                        label={vendor.vendor_code} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                      <Chip
                        label={vendor.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={vendor.is_active ? 'success' : 'default'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Support: {vendor.support_phone} | Contact: {vendor.contact_email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Default Tax: {vendor.default_tax_code || 'HST'} ({vendor.default_tax_rate || 13}%)
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton onClick={() => handleEditVendor(vendor)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDeleteVendor(vendor)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton onClick={() => toggleVendorExpanded(vendor.id)}>
                      {expandedVendors.has(vendor.id) ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                  </Box>
                </Box>
                
                {expandedVendors.has(vendor.id) && (
                  <VendorServicesDisplay vendor={vendor} />
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const RetailServicesTab = () => {
    const retailServices = vendorServices.filter(s => s.retail_service?.is_active);
    
    return (
      <Box>
        <Typography variant="h6" mb={3}>Active Retail Services</Typography>
        
        <Grid container spacing={3}>
          {['vdsl', 'fibre', 'cable'].map((type) => (
            <Grid item xs={12} md={4} key={type}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {type.toUpperCase()} Services
                  </Typography>
                  <List dense>
                    {retailServices
                      .filter(s => s.service_type === type)
                      .map((service) => (
                        <ListItem key={service.id}>
                          <ListItemText
                            primary={service.retail_service?.retail_name}
                            secondary={
                              <>
                                ${service.retail_service?.retail_monthly_price}/mo
                                <Chip
                                  label={`${toNumber(service.retail_service?.margin_percentage || 0).toFixed(1)}% margin`}
                                  size="small"
                                  sx={{ ml: 1, height: 18 }}
                                />
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const MarginAnalysisTab = () => {
    const avgMargin = vendorServices
      .filter(s => s.retail_service)
      .reduce((sum, s) => sum + toNumber(s.retail_service?.margin_percentage || 0), 0) / 
      vendorServices.filter(s => s.retail_service).length;
    
    return (
      <Box>
        <Typography variant="h6" mb={3}>Margin Analysis</Typography>
        
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Average Margin
                </Typography>
                <Typography variant="h4">
                  {avgMargin.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Service</TableCell>
                <TableCell align="right">Wholesale</TableCell>
                <TableCell align="right">Retail</TableCell>
                <TableCell align="right">Margin $</TableCell>
                <TableCell align="right">Margin %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendorServices
                .filter(s => s.retail_service)
                .map((service) => (
                  <TableRow key={service.id}>
                    <TableCell>{service.vendor_name}</TableCell>
                    <TableCell>{service.retail_service?.retail_name}</TableCell>
                    <TableCell align="right">${service.wholesale_monthly_cost}</TableCell>
                    <TableCell align="right">${service.retail_service?.retail_monthly_price}</TableCell>
                    <TableCell align="right">${toNumber(service.retail_service?.monthly_margin || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${toNumber(service.retail_service?.margin_percentage || 0).toFixed(1)}%`}
                        size="small"
                        color={toNumber(service.retail_service?.margin_percentage || 0) > 30 ? 'success' : 'warning'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };
  
  const loadTaxData = async () => {
    try {
      // Load customers
      const customersRes = await api.get('/endpoints/customers');
      setCustomers(customersRes.data.data || []);
      
      // Load tax overrides
      const overridesRes = await api.get('/endpoints/vendor_tax_overrides');
      setTaxOverrides(overridesRes.data.data || []);
    } catch (err: any) {
      console.error('Failed to load tax data:', err);
      setError('Failed to load tax settings');
    }
  };
  
  const CustomerTaxSettings = () => {
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | ''>('');
    const [vendorTaxSettings, setVendorTaxSettings] = useState<{[key: number]: {code: string, rate: number}}>({});
    
    // Get customer's current tax overrides
    const getCustomerOverrides = (customerId: number) => {
      return taxOverrides.filter(o => o.customer_id === customerId);
    };
    
    // Initialize vendor tax settings when customer is selected
    useEffect(() => {
      if (selectedCustomerId) {
        const overrides = getCustomerOverrides(selectedCustomerId as number);
        const settings: {[key: number]: {code: string, rate: number}} = {};
        
        // Set defaults for all vendors
        vendors.forEach(vendor => {
          const override = overrides.find(o => o.vendor_id === vendor.id);
          if (override) {
            settings[vendor.id] = { code: override.tax_code, rate: override.tax_rate };
          } else {
            settings[vendor.id] = { code: vendor.default_tax_code || 'HST', rate: vendor.default_tax_rate || 13 };
          }
        });
        
        setVendorTaxSettings(settings);
      }
    }, [selectedCustomerId]);
    
    const handleTaxChange = async (vendorId: number, taxCode: string) => {
      if (!selectedCustomerId) return;
      
      const taxRate = taxCode === 'HST' ? 13 : 5;
      
      try {
        await api.post('/endpoints/vendor_tax_overrides', {
          customer_id: selectedCustomerId,
          vendor_id: vendorId,
          tax_code: taxCode,
          tax_rate: taxRate
        });
        
        setSuccess('Tax settings updated');
        loadTaxData();
        
        // Update local state
        setVendorTaxSettings(prev => ({
          ...prev,
          [vendorId]: { code: taxCode, rate: taxRate }
        }));
      } catch (err: any) {
        setError('Failed to update tax settings');
      }
    };
    
    const selectedCustomerData = customers.find(c => c.id === selectedCustomerId);
    
    return (
      <Box>
        <Typography variant="h6" mb={3}>Customer Tax Settings</Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Set tax rates for each vendor based on customer location. Default is HST (13%) for Ontario. 
          Customers in Alberta, Northwest Territories, Nunavut, and Yukon use GST (5%).
        </Alert>
        
        <FormControl fullWidth sx={{ mb: 3, maxWidth: 400 }}>
          <InputLabel>Select Customer</InputLabel>
          <Select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value as number)}
            label="Select Customer"
          >
            <MenuItem value="">-- Select a Customer --</MenuItem>
            {customers.map(customer => (
              <MenuItem key={customer.id} value={customer.id}>
                {customer.company} {customer.province ? `(${customer.province})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {selectedCustomerId && (
          <>
            {selectedCustomerData && (
              <Card sx={{ mb: 3, backgroundColor: 'background.default' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {selectedCustomerData.company}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCustomerData.city}{selectedCustomerData.province ? `, ${selectedCustomerData.province}` : ''}
                  </Typography>
                </CardContent>
              </Card>
            )}
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Default Tax</TableCell>
                    <TableCell>Customer Tax Override</TableCell>
                    <TableCell>Effective Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendors.map(vendor => {
                    const settings = vendorTaxSettings[vendor.id] || { 
                      code: vendor.default_tax_code || 'HST', 
                      rate: vendor.default_tax_rate || 13 
                    };
                    
                    return (
                      <TableRow key={vendor.id}>
                        <TableCell>
                          <Typography variant="body2">{vendor.vendor_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {vendor.vendor_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${vendor.default_tax_code || 'HST'} (${vendor.default_tax_rate || 13}%)`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={settings.code}
                              onChange={(e) => handleTaxChange(vendor.id, e.target.value)}
                              variant="outlined"
                            >
                              <MenuItem value="HST">HST (13%)</MenuItem>
                              <MenuItem value="GST">GST (5%)</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${settings.rate}%`}
                            color={settings.code === 'GST' ? 'warning' : 'primary'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>Internet Vendor Management</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Vendors" icon={<BusinessIcon />} iconPosition="start" />
            <Tab label="Retail Services" icon={<RouterIcon />} iconPosition="start" />
            <Tab label="Margin Analysis" icon={<TrendingUpIcon />} iconPosition="start" />
            <Tab label="Customer Tax Settings" icon={<MoneyIcon />} iconPosition="start" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <VendorsTab />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <RetailServicesTab />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <MarginAnalysisTab />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <CustomerTaxSettings />
          </TabPanel>
        </CardContent>
      </Card>

      {/* Vendor Dialog */}
      <Dialog open={vendorDialog} onClose={() => setVendorDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Vendor Name"
                value={vendorForm.vendor_name}
                onChange={(e) => setVendorForm({ ...vendorForm, vendor_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vendor Code"
                value={vendorForm.vendor_code}
                onChange={(e) => setVendorForm({ ...vendorForm, vendor_code: e.target.value.toUpperCase() })}
                helperText="Short identifier (e.g., FIBER)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Support Phone"
                value={vendorForm.support_phone}
                onChange={(e) => setVendorForm({ ...vendorForm, support_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={vendorForm.contact_email}
                onChange={(e) => setVendorForm({ ...vendorForm, contact_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Default Tax Code</InputLabel>
                <Select
                  value={vendorForm.default_tax_code}
                  onChange={(e) => {
                    const taxCode = e.target.value;
                    const taxRate = taxCode === 'HST' ? 13 : 5;
                    setVendorForm({ ...vendorForm, default_tax_code: taxCode, default_tax_rate: taxRate });
                  }}
                  label="Default Tax Code"
                >
                  <MenuItem value="HST">HST (13%)</MenuItem>
                  <MenuItem value="GST">GST (5%)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax Rate (%)"
                type="number"
                value={vendorForm.default_tax_rate}
                disabled
                helperText="Rate is set based on tax code"
                sx={numberInputStyle}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={vendorForm.is_active}
                    onChange={(e) => setVendorForm({ ...vendorForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVendorDialog(false)} disabled={saving}>Cancel</Button>
          <Button 
            onClick={handleSaveVendor} 
            variant="contained"
            disabled={saving || !vendorForm.vendor_name.trim()}
          >
            {saving ? 'Saving...' : (selectedVendor ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Service Dialog */}
      <Dialog open={serviceDialog} onClose={() => setServiceDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedService ? 'Edit Service' : 'Add Service'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Service Details</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Service Type</InputLabel>
                <Select
                  value={serviceForm.service_type}
                  onChange={(e) => handleServiceFormChange('service_type', e.target.value)}
                  label="Service Type"
                >
                  <MenuItem value="vdsl">VDSL</MenuItem>
                  <MenuItem value="fibre">Fibre</MenuItem>
                  <MenuItem value="cable">Cable</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Download Speed (Mbps)"
                type="number"
                value={serviceForm.speed_down}
                onChange={(e) => handleServiceFormChange('speed_down', e.target.value)}
                sx={numberInputStyle}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Upload Speed (Mbps)"
                type="number"
                value={serviceForm.speed_up}
                onChange={(e) => handleServiceFormChange('speed_up', e.target.value)}
                sx={numberInputStyle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Speed Label"
                value={serviceForm.speed_label}
                onChange={(e) => handleServiceFormChange('speed_label', e.target.value)}
                helperText="e.g., 50M, 1GB"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Technology Details"
                value={serviceForm.technology_details}
                onChange={(e) => handleServiceFormChange('technology_details', e.target.value)}
                helperText="e.g., Bell Nexia VDSL"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Wholesale Costs</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Monthly Cost"
                type="number"
                value={serviceForm.wholesale_monthly_cost}
                onChange={(e) => handleServiceFormChange('wholesale_monthly_cost', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Setup Fee"
                type="number"
                value={serviceForm.wholesale_setup_fee}
                onChange={(e) => handleServiceFormChange('wholesale_setup_fee', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Modem Cost"
                type="number"
                value={serviceForm.wholesale_modem_cost}
                onChange={(e) => handleServiceFormChange('wholesale_modem_cost', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Retail Pricing (Auto-calculated)</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Retail Service Name"
                value={serviceForm.retail_name}
                onChange={(e) => handleServiceFormChange('retail_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Retail Description"
                multiline
                rows={2}
                value={serviceForm.retail_description}
                onChange={(e) => handleServiceFormChange('retail_description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Retail Monthly Price"
                type="number"
                value={serviceForm.retail_monthly_price}
                onChange={(e) => handleServiceFormChange('retail_monthly_price', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                helperText={`Margin: $${(toNumber(serviceForm.retail_monthly_price) - toNumber(serviceForm.wholesale_monthly_cost)).toFixed(2)} (${calculateMargin(toNumber(serviceForm.retail_monthly_price), toNumber(serviceForm.wholesale_monthly_cost)).toFixed(1)}%)`}
                sx={numberInputStyle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Retail Setup Fee"
                type="number"
                value={serviceForm.retail_setup_fee}
                onChange={(e) => handleServiceFormChange('retail_setup_fee', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveService} variant="contained">
            {selectedService ? 'Update' : 'Create Service & Retail Pricing'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InternetVendorManagementV2;